// src/components/km/ResourceFittingPanel.jsx
//
// Punteggio di fitting di un candidato su un annuncio, nel dettaglio
// candidato di Console HR — 01/10, richiesto esplicitamente.
//
// NESSUNA modifica backend: tutto quello che serve esiste già.
// - GET /targets                         → elenco annunci attivi (per il
//   selettore; solo quelli con almeno un punteggio, viene da v_jd_ranking)
// - GET /resources/{id}?jd_id=<jd>        → blocco "fitting" già previsto
//   da targets.get_resource (punteggio, rating, reasoning, req_present,
//   req_missing) quando si passa un jd_id — lo stesso usato dal SidePanel
//   di MultifittingDashboard.
//
// A differenza del Cross-fit del SidePanel (che mostra TUTTI gli annunci in
// una volta, perché lì i dati sono già in memoria da /api/multifitting/nightly),
// qui si sceglie un annuncio alla volta: Console HR non carica quel dataset,
// e andarlo a caricare solo per questo sarebbe uno spreco. Un annuncio per
// volta copre la richiesta ("vedere il punteggio di fitting di un
// candidato") senza introdurre nuove chiamate pesanti né toccare il server.
//
// Il fitting resta legato a un annuncio per costruzione (è un match
// candidato↔requisiti di una posizione specifica): non esiste un "punteggio
// di fitting" del candidato in assoluto, a differenza del Voto CV.

import React, { useState } from 'react';
import { useRealKmClient } from '../../lib/useRealKmClient.js';

const T = {
  border: 'rgba(0,0,0,0.12)',
  surface: '#FFFFFF',
  surface2: '#F1F0ED',
  txt: '#1A1916',
  txt2: '#6B6A65',
  txt3: '#A3A19C',
  greenBg: '#EAF3DE',
  greenTxt: '#27500A',
  amberBg: '#FAEEDA',
  amberTxt: '#633806',
  redBg: '#FCEBEB',
  redTxt: '#791F1F',
  grayBg: '#F1F0ED',
  grayTxt: '#6B6A65',
};

// Stessa mappa fascia→colore di MultifittingDashboard/FittingBadge, tenuta
// qui in locale per non far dipendere km/ da un file fuori da questa cartella.
function FittingBadge({ fitting }) {
  const map = {
    OTTIMO: [T.greenBg, T.greenTxt],
    BUONO: ['#d4edda', '#155724'],
    SUFFICIENTE: [T.amberBg, T.amberTxt],
    PARZIALE: ['#fde8d8', '#7d3b00'],
    INADEGUATO: [T.redBg, T.redTxt],
  };
  const [bg, fg] = map[fitting] || [T.grayBg, T.grayTxt];
  return (
    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: bg, color: fg, whiteSpace: 'nowrap' }}>
      {fitting || '—'}
    </span>
  );
}

export default function ResourceFittingPanel({ resourceId }) {
  const kmClient = useRealKmClient();
  const [annunci, setAnnunci] = useState([]);
  const [statusAnnunci, setStatusAnnunci] = useState('loading'); // loading | ready | error
  const [jdId, setJdId] = useState('');
  const [fitting, setFitting] = useState(null);
  const [statusFitting, setStatusFitting] = useState('idle'); // idle | loading | ready | error
  const [errorMessage, setErrorMessage] = useState(null);

  React.useEffect(() => {
    kmClient
      .get('/targets', { query: { limite: 200 } })
      .then((data) => setAnnunci(Array.isArray(data?.target) ? data.target : []))
      .catch(() => setAnnunci([]))
      .finally(() => setStatusAnnunci('ready'));
  }, [kmClient]);

  React.useEffect(() => {
    if (!resourceId || !jdId) {
      setFitting(null);
      setStatusFitting('idle');
      return;
    }
    setStatusFitting('loading');
    setErrorMessage(null);
    kmClient
      .get(`/resources/${encodeURIComponent(resourceId)}`, { query: { jd_id: jdId } })
      .then((data) => {
        setFitting(data?.fitting || null);
        setStatusFitting('ready');
      })
      .catch((err) => {
        setErrorMessage(err?.detail || 'Errore nel calcolo del fitting.');
        setStatusFitting('error');
      });
  }, [kmClient, resourceId, jdId]);

  if (!resourceId) return null;

  const reqPresent = Array.isArray(fitting?.req_present) ? fitting.req_present : [];
  const reqMissing = Array.isArray(fitting?.req_missing) ? fitting.req_missing : [];

  return (
    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: T.surface, border: `0.5px solid ${T.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.txt3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Fitting su annuncio
      </div>

      <select
        value={jdId}
        onChange={(e) => setJdId(e.target.value)}
        disabled={statusAnnunci === 'loading'}
        style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 13, marginBottom: 8 }}
      >
        <option value="">— seleziona un annuncio —</option>
        {annunci.map((a) => (
          <option key={a.jd_id} value={a.jd_id}>{a.titolo || a.jd_id}</option>
        ))}
      </select>

      {statusAnnunci === 'ready' && annunci.length === 0 && (
        <div style={{ fontSize: 12, color: T.txt3 }}>Nessun annuncio con punteggi disponibili al momento.</div>
      )}

      {statusFitting === 'loading' && (
        <div style={{ fontSize: 12, color: T.txt2 }}>Calcolo in corso…</div>
      )}

      {statusFitting === 'error' && (
        <div style={{ fontSize: 12, color: T.redTxt }}>⚠ {errorMessage}</div>
      )}

      {statusFitting === 'ready' && jdId && !fitting && (
        <div style={{ fontSize: 12, color: T.txt3 }}>
          Fitting non ancora calcolato per questo candidato su questo annuncio.
        </div>
      )}

      {statusFitting === 'ready' && fitting && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1 }}>{fitting.punteggio}</span>
            <div>
              <div style={{ fontSize: 10, color: T.txt3 }}>Punteggio · posizione #{fitting.posizione ?? '—'}</div>
              <FittingBadge fitting={fitting.rating} />
            </div>
          </div>

          {fitting.reasoning && (
            <div style={{ fontSize: 12, lineHeight: 1.5, color: T.txt2, marginBottom: 8 }}>
              🧠 {fitting.reasoning}
            </div>
          )}

          {reqPresent.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.txt3, textTransform: 'uppercase', marginBottom: 4 }}>Requisiti presenti</div>
              {reqPresent.map((r, i) => (
                <div key={i} style={{ fontSize: 12, marginBottom: 2 }}><span style={{ color: T.greenTxt }}>✓</span> {r}</div>
              ))}
            </div>
          )}

          {reqMissing.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, color: T.txt3, textTransform: 'uppercase', marginBottom: 4 }}>Requisiti assenti</div>
              {reqMissing.map((r, i) => (
                <div key={i} style={{ fontSize: 12, marginBottom: 2 }}><span style={{ color: T.redTxt }}>✗</span> {r}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
